import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import DashboardContainer from './components/Dashboard/DashboardContainer';
import Inbox from './components/Inbox/Inbox';
import Leads from './components/Leads/Leads';
import Automations from './components/Automations';
import CaptureTools from './components/capture-tools/CaptureTools';
import Settings from './components/Settings/Settings';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import CompleteProfile from './components/Auth/CompleteProfile';
import ForgotPassword from './components/Auth/ForgotPassword';
import PublicPortfolioViewer from './components/public/PublicPortfolioViewer';
import LandingPage from './components/landing/LandingPage';
import IntegrationsDocs from './pages/IntegrationsDocs';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import { useAuth } from './context/AuthContext';
import { getSubdomainInfo } from './utils/subdomain';

    
// Shared loading screen
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
      <p>Loading...</p>
    </div>
  </div>
);

const hasProfileName = (profile) => {
  const fullName = typeof profile?.fullName === 'string' ? profile.fullName.trim() : '';
  const name = typeof profile?.name === 'string' ? profile.name.trim() : '';
  return !!(fullName || name);
};

// Protected route - requires authentication + complete profile
const ProtectedRoute = () => {
  const { user, loading, userProfile, userProfileLoading, profileHydrated } = useAuth();
  const location = useLocation();
  const profileCompleted = userProfile?.onboardingCompleted === true && hasProfileName(userProfile);
  const shouldWaitForProfile = !!user && (!profileHydrated || userProfileLoading);

  console.log('[route.guard.debug]', {
    route: location.pathname,
    user: user ? { uid: user.uid, email: user.email || null } : null,
    profile: userProfile,
    loading,
    userProfileLoading,
    profileHydrated,
    profileCompleted,
    shouldRedirect: !!user && profileHydrated && !userProfileLoading && !profileCompleted,
  });

  if (loading || shouldWaitForProfile) {
    return <LoadingScreen />;
  }

  if (!user) {
    console.info('[route.guard]', {
      event: 'redirect_to_login',
      reason: 'no_auth_user',
      timestamp: new Date().toISOString(),
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!profileCompleted) {
    console.info('[route.guard]', {
      event: 'redirect_to_complete_profile',
      uid: user.uid,
      hasProfile: !!userProfile,
      onboardingCompleted: userProfile?.onboardingCompleted ?? null,
      timestamp: new Date().toISOString(),
    });
    return <Navigate to="/complete-profile" replace />;
  }

  console.info('[route.guard]', {
    event: 'dashboard_access_granted',
    uid: user.uid,
    timestamp: new Date().toISOString(),
  });

  return <Outlet />;
};

// Profile completion route - requires auth but incomplete/missing profile
const CompleteProfileRoute = ({ children }) => {
  const { user, loading, userProfile, userProfileLoading, profileHydrated } = useAuth();
  const profileCompleted = userProfile?.onboardingCompleted === true && hasProfileName(userProfile);
  const shouldWaitForProfile = !!user && (!profileHydrated || userProfileLoading);

  console.log('[complete-profile.guard.debug]', {
    user: user ? { uid: user.uid, email: user.email || null } : null,
    profile: userProfile,
    loading,
    userProfileLoading,
    profileHydrated,
    profileCompleted,
    shouldRedirectToDashboard: !!user && profileHydrated && !userProfileLoading && profileCompleted,
  });

  if (loading || shouldWaitForProfile) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (profileCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public route - redirects authenticated users to dashboard
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return user ? <Navigate to="/dashboard" replace /> : children;
};

// Admin protected route - checks for admin JWT in sessionStorage
const AdminProtectedRoute = ({ children }) => {
  const token = sessionStorage.getItem('adminToken');
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function App() {
  const subdomainInfo = getSubdomainInfo();

  // Public subdomain: render PublicPortfolioViewer for all routes
  if (subdomainInfo.isPublicDomain) {
    return (
      <Routes>
        <Route path="/" element={<PublicPortfolioViewer />} />
        <Route path="/property/:propertyId" element={<PublicPortfolioViewer />} />
        <Route path="*" element={<PublicPortfolioViewer />} />
      </Routes>
    );
  }

  // App domain: standard authenticated routing with public landing page
  return (
    <Routes>
      {/* Public landing page - redirects to dashboard if authenticated */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/integrations" element={<IntegrationsDocs />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/complete-profile" element={<CompleteProfileRoute><CompleteProfile /></CompleteProfileRoute>} />

      {/* Public portfolio access via direct link */}
      <Route path="/public/:portfolioId" element={<PublicPortfolioViewer />} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Layout />}>
          <Route index element={<DashboardContainer />} />
        </Route>
        <Route element={<Layout />}>
          <Route path="inbox" element={<Inbox />} />
          <Route path="leads" element={<Leads />} />
          <Route path="automations" element={<Automations />} />
          <Route path="capture" element={<CaptureTools />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Route>

      {/* Fallback: redirect unknown routes to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

export default App;

